import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getSimilarProfiles, getProfileFromUser } from '@/api/profile';
import { getAPIConfig } from '@/api/config';
import { RootState } from '@/store';
import { AvatarImage } from '@/components/layout/AvatarImage';
import Loader from '@/components/ui/loader';
import { Music, Disc3, Wrench } from 'lucide-react';
import { createPortal } from 'react-dom';
import axiosService from '@/utils/axios';
import { getUserDisplayName } from '@/utils/userDisplay';
import { formatGenreLabel, formatGenreList } from '@/utils/genreLabel';

// Add a helper function to get skill data from the backend
async function getSkillsByIds(skillIds: string[]) {
    try {
        const uniqueIds = [...new Set(skillIds)];
        if (uniqueIds.length === 0) return {};

        const { data } = await axiosService.post('/skills/batch', { ids: uniqueIds });

        // Create a map of skill ID to skill name
        const skillsMap: Record<string, string> = {};
        if (Array.isArray(data)) {
            data.forEach(skill => {
                if (skill._id && (skill.displayName || skill.name)) {
                    skillsMap[skill._id] = skill.displayName || skill.name;
                }
            });
        }

        return skillsMap;
    } catch (error) {
        console.error('Error fetching skills data:', error);
        return {};
    }
}

// Define type for profile objects based on MongoDB schema
interface ProfileType {
  _id: string;
  profilePhoto?: string;
  displayName?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  user: any;
  relevanceScore?: number;
  similarAttributes?: string[];
  skills?: string[]; // Array of skill ObjectIDs from the skills collection
  favArtists?: {
    id?: string;
    name: string;
    url?: string;
    displayText?: string;
  }[]; // Array of artist objects as defined in MongoDB schema
  favGenres?: string[]; // Array of genre strings
  _categorizedAttributes?: CategorizedAttributes; // Add this to fix the linter error
  // Add a new field to store typed attributes for better categorization
  _typedAttributes?: Array<{
    type: 'skill' | 'genre' | 'artist';
    value: string;
    rawValue?: string;
  }>;
  backendRandomized?: boolean; // Add flag to check if backend has already applied randomization
}

// Add a new interface for the additional properties we add to the profiles
interface ProfileTypeWithRandomness extends ProfileType {
  _originalScore?: number;
}

interface SimilarArtistsProps {
  userId: string;
  username: string;
  /** Same string as the profile header, from getUserDisplayName. */
  displayName: string;
}

// Add categorization for common interests
type CategorizedAttributes = {
  artists: string[];
  genres: string[];
  skills: string[];
};

// Add a new function to apply randomness to similarity scores on the frontend
function applyRandomnessToProfiles(profiles: ProfileType[], minFactor: number, maxFactor: number): ProfileType[] {
    // Skip randomization if backend has already applied it
    // Check the first profile to determine if backend randomization was applied
    if (profiles.length > 0 && profiles[0].backendRandomized) {
        return profiles;
    }

    // Create a copy of the profiles to avoid mutating the original data
    const profilesWithRandomness = profiles.map(profile => {
    // Clone the profile to avoid mutation
        const profileCopy: ProfileTypeWithRandomness = { ...profile };

        // Generate a random value between min and max from config
        const randomFactor = minFactor + Math.random() * (maxFactor - minFactor);

        // Add the random factor to the relevance score
        if (typeof profileCopy.relevanceScore === 'number') {
            profileCopy._originalScore = profileCopy.relevanceScore; // Store original for debugging
            profileCopy.relevanceScore += randomFactor;
        }

        return profileCopy;
    });

    // Sort by the new randomized scores
    return profilesWithRandomness.sort((a, b) =>
        (b.relevanceScore || 0) - (a.relevanceScore || 0)
    );
}

const SimilarArtists = ({ userId, displayName }: SimilarArtistsProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [similarProfiles, setSimilarProfiles] = useState<ProfileType[]>([]);
    const [error, setError] = useState('');
    const [tooltipProfileId, setTooltipProfileId] = useState<string | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [mainProfileData, setMainProfileData] = useState<any>(null);
    const [skillsData, setSkillsData] = useState<Record<string, string>>({});
    const [componentMounted, setComponentMounted] = useState(false);
    const [randomizationEnabled, setRandomizationEnabled] = useState(false);
    const [randomFactorMin, setRandomFactorMin] = useState(0);
    const [randomFactorMax, setRandomFactorMax] = useState(0);
    const [configLoaded, setConfigLoaded] = useState(false);

    // Add a ref to track which request combinations we've already made
    const requestsMadeRef = useRef<Set<string>>(new Set());
    const previousUserIdRef = useRef(userId);
    if (previousUserIdRef.current !== userId) {
        previousUserIdRef.current = userId;
        requestsMadeRef.current.clear();
        setSimilarProfiles([]);
        setMainProfileData(null);
        setSkillsData({});
        setError('');
        setIsLoading(true);
        setTooltipProfileId(null);
    }
    // Add a ref to track if the component is mounted
    const isMountedRef = useRef<boolean>(true);
    // Add a timeout ref to force exit loading state
    const loadingTimeoutRef = useRef<any>(null);

    // Get current user to avoid showing in similar profiles
    const currentUser = useSelector((state: RootState) => state.auth.user);

    // First effect - component mount/unmount logic and fetch config
    useEffect(() => {
        isMountedRef.current = true;
        setComponentMounted(true);

        // Fetch randomization configuration from the API
        const fetchConfig = async () => {
            try {
                const config = await getAPIConfig();
                if (isMountedRef.current) {
                    setRandomizationEnabled(config.RANDOMIZE_SIMILAR_ARTISTS);
                    setRandomFactorMin(config.RANDOMNESS_FACTOR_MIN);
                    setRandomFactorMax(config.RANDOMNESS_FACTOR_MAX);
                    setConfigLoaded(true);
                }
            } catch (error) {
                setConfigLoaded(true);
            }
        };

        fetchConfig();

        loadingTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
                setIsLoading(false);
                if (similarProfiles.length === 0) {
                    setError('Loading timeout reached. Please refresh to try again.');
                }
            }
        }, 10000);

        return () => {
            isMountedRef.current = false;
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
        };
    }, [similarProfiles.length]);

    // Fetch the main profile data to compare with similar profiles
    useEffect(() => {
        if (!componentMounted) return;

        const fetchMainProfile = async () => {
            if (!userId) {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
                return;
            }

            const requestKey = `mainProfile-${userId}`;
            if (requestsMadeRef.current.has(requestKey)) {
                return;
            }

            requestsMadeRef.current.add(requestKey);

            try {
                const data = await getProfileFromUser(userId);
                if (isMountedRef.current) {
                    if (!data) {
                        setError('Could not load profile data');
                        setIsLoading(false);
                        return;
                    }
                    setMainProfileData(data);
                }
            } catch (error) {
                if (isMountedRef.current) {
                    setError('Failed to load profile information');
                    setIsLoading(false);
                }
            }
        };

        fetchMainProfile();
    }, [userId, componentMounted]);

    // Fetch similar profiles when main profile data is available
    useEffect(() => {
        if (!componentMounted || !mainProfileData) return;

        const fetchSimilarProfiles = async () => {
            if (!userId) {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
                return;
            }

            const requestKey = `similarProfiles-${userId}`;
            if (requestsMadeRef.current.has(requestKey)) {
                return;
            }

            requestsMadeRef.current.add(requestKey);

            try {
                const data = await getSimilarProfiles(userId, 0, 'relevance', 6);

                if (isMountedRef.current) {
                    if (loadingTimeoutRef.current) {
                        clearTimeout(loadingTimeoutRef.current);
                        loadingTimeoutRef.current = null;
                    }

                    if (!data || !data.profiles) {
                        setError('Could not load similar profile data');
                        setIsLoading(false);
                        return;
                    }

                    const validProfiles: ProfileType[] = data.profiles.filter((profile: ProfileType) => !!profile._id);
                    const displayProfiles = randomizationEnabled ?
                        applyRandomnessToProfiles(validProfiles, randomFactorMin, randomFactorMax) : validProfiles;
                    const limitedProfiles = displayProfiles.slice(0, 6);

                    const processedProfiles = limitedProfiles.map(profile => {
                        const updatedProfile = { ...profile };
                        if (updatedProfile.similarAttributes &&
                            (!updatedProfile._categorizedAttributes ||
                             (updatedProfile._categorizedAttributes.artists.length === 0 &&
                              updatedProfile._categorizedAttributes.genres.length === 0 &&
                              updatedProfile._categorizedAttributes.skills.length === 0))) {
                            updatedProfile._categorizedAttributes = categorizeAttributes(updatedProfile.similarAttributes, updatedProfile);
                        }
                        return updatedProfile;
                    });

                    setSimilarProfiles(processedProfiles);
                    setIsLoading(false);

                    const allSkillIds: string[] = [];
                    processedProfiles.forEach((profile: ProfileType) => {
                        if (profile.skills && Array.isArray(profile.skills)) {
                            profile.skills.forEach((skill: string) => {
                                if (typeof skill === 'string' && isObjectId(skill)) {
                                    allSkillIds.push(skill);
                                }
                            });
                        }

                        if (profile.similarAttributes && Array.isArray(profile.similarAttributes)) {
                            profile.similarAttributes.forEach((attr: string) => {
                                if (typeof attr === 'string' && isObjectId(attr)) {
                                    allSkillIds.push(attr);
                                }
                            });
                        }
                    });

                    if (allSkillIds.length > 0) {
                        getSkillsByIds(allSkillIds).then(skillsMap => {
                            if (isMountedRef.current) {
                                setSkillsData(skillsMap);
                                setSimilarProfiles(prevProfiles =>
                                    prevProfiles.map(profile => {
                                        const updatedProfile = { ...profile };
                                        if (updatedProfile.similarAttributes) {
                                            updatedProfile.similarAttributes = updatedProfile.similarAttributes.map(attr => {
                                                if (isObjectId(attr) && skillsMap[attr]) {
                                                    return skillsMap[attr];
                                                }
                                                return attr;
                                            });
                                            updatedProfile._categorizedAttributes = categorizeAttributes(updatedProfile.similarAttributes, updatedProfile);
                                        }
                                        return updatedProfile;
                                    })
                                );
                            }
                        });
                    }
                }
            } catch (error) {
                if (isMountedRef.current) {
                    setError('Couldn\'t load people like this');
                    setIsLoading(false);
                }
            }
        };

        fetchSimilarProfiles();
    }, [userId, mainProfileData, componentMounted, randomizationEnabled, randomFactorMin, randomFactorMax]);

    const profileName = displayName.trim();
    const sectionTitle = profileName ? `People Like ${profileName}` : 'People Like this';
    const findingText = profileName ? `Finding people like ${profileName}...` : 'Finding people...';

    // Render logic
    if (!componentMounted) {
        return (
            <div className="bg-white rounded-lg p-4">
                <h2 className="mb-4 break-words text-lg font-bold text-eerieblack">{sectionTitle}</h2>
                <div className="flex justify-center py-6">
                    <Loader size="md" text={findingText} />
                </div>
            </div>
        );
    }

    if (!isLoading && (!similarProfiles || similarProfiles.length === 0) && !error) {
        return null;
    }

    const showTooltip = (profileId: string, event: React.MouseEvent) => {
        if (tooltipProfileId !== profileId) {
            setTooltipProfileId(profileId);
            const profileCard = (event.currentTarget.closest('.similar-artist-card') as HTMLElement);
            if (profileCard) {
                const rect = profileCard.getBoundingClientRect();
                setTooltipPosition({
                    x: rect.left + (rect.width / 2),
                    y: rect.top
                });
            } else {
                const rect = event.currentTarget.getBoundingClientRect();
                setTooltipPosition({
                    x: rect.left + (rect.width / 2),
                    y: rect.top
                });
            }
        }
    };

    const hideTooltip = () => {
        setTooltipProfileId(null);
    };

    return (
        <div className="w-full">
            <div className="bg-white rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="break-words text-lg font-bold text-eerieblack">{sectionTitle}</h2>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-6">
                        <Loader size="md" text={findingText} />
                    </div>
                ) : similarProfiles.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {similarProfiles.map((profile) => {
                            const profileId = profile._id;
                            const username = profile.user?.username || profile.username || 'unknown';

                            // Use getUserDisplayName utility for consistent display name handling
                            const displayName = getUserDisplayName({
                                displayName: profile.displayName,
                                firstName: profile.firstName || profile.user?.firstName,
                                lastName: profile.lastName || profile.user?.lastName,
                                username: username,
                                fallback: username
                            });

                            const photoUrl = profile.profilePhoto || profile.user?.profilePhoto;
                            const hasMultipleAttributes = profile.similarAttributes &&
                                           countReadableAttributes(profile.similarAttributes) > 1;

                            return (
                                <Link
                                    key={profileId}
                                    to={`/${username}`}
                                    className="flex flex-col items-center cursor-pointer hover:opacity-80 transition-opacity p-2 hover:bg-gray-50 rounded-lg relative similar-artist-card no-underline text-inherit"
                                    onMouseEnter={(event) => showTooltip(profileId, event)}
                                    onMouseLeave={hideTooltip}
                                >
                                    <div className="h-20 w-20 mb-2">
                                        <AvatarImage
                                            src={photoUrl}
                                            alt={displayName}
                                            testId={`similar-profile-${profileId}`}
                                        />
                                    </div>
                                    <p className="text-center font-medium text-sm truncate max-w-full">
                                        {displayName}
                                    </p>
                                    {profile.similarAttributes && profile.similarAttributes.length > 0 && findFirstReadableAttribute(profile.similarAttributes) && (
                                        <div className="text-xs text-gray-500 mt-1 text-center">
                                            <span className="inline-block text-center">
                                                <span className="opacity-75">In common:</span>{' '}
                                                <span className="font-medium">{findFirstReadableAttribute(profile.similarAttributes)}</span>
                                                {hasMultipleAttributes && (
                                                    <span
                                                        className="ml-1 cursor-pointer text-indigo-600 hover:text-indigo-800 transition-colors hover:underline"
                                                        onMouseEnter={(event) => showTooltip(profileId, event)}
                                                        onMouseLeave={hideTooltip}
                                                    >
                            +{countReadableAttributes(profile.similarAttributes) - 1} more
                                                    </span>
                                                )}
                                            </span>

                                            {hasMultipleAttributes && tooltipProfileId === profileId &&
                        createPortal(
                            <div
                                style={{
                                    position: 'fixed',
                                    left: `${tooltipPosition.x}px`,
                                    top: `${tooltipPosition.y}px`,
                                    transform: 'translate(-50%, -100%)',
                                    marginTop: '-8px',
                                    backgroundColor: '#FFFFFF',
                                    color: '#333333',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                                    border: '1px solid #E5E7EB',
                                    zIndex: 99999,
                                    minWidth: '200px',
                                    maxWidth: '280px'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div style={{
                                    fontWeight: 500,
                                    textAlign: 'center',
                                    marginBottom: '4px',
                                    color: '#4F46E5',
                                    borderBottom: '1px solid #F3F4F6',
                                    paddingBottom: '4px'
                                }}>Common Interests</div>

                                {(() => {
                                    let categorized = profile._categorizedAttributes || { artists: [], genres: [], skills: [] };

                                    if ((!categorized.artists?.length && !categorized.genres?.length && !categorized.skills?.length) &&
                                  profile.similarAttributes && profile.similarAttributes.length > 0) {
                                        categorized = categorizeAttributes(profile.similarAttributes, profile);
                                        profile._categorizedAttributes = categorized;
                                    }

                                    categorized = {
                                        artists: categorized.artists || [],
                                        genres: categorized.genres || [],
                                        skills: categorized.skills || []
                                    };

                                    return (
                                        <div style={{ textAlign: 'left' }}>
                                            {categorized.artists.length > 0 && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <div style={{
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem',
                                                        color: '#374151',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}>
                                                        <Music style={{ height: '14px', width: '14px', marginRight: '4px' }} />
                                        Artists:
                                                    </div>
                                                    <div style={{ paddingLeft: '8px', color: '#4B5563' }}>
                                                        {categorized.artists.join(', ')}
                                                    </div>
                                                </div>
                                            )}

                                            {categorized.genres.length > 0 && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <div style={{
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem',
                                                        color: '#374151',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}>
                                                        <Disc3 style={{ height: '14px', width: '14px', marginRight: '4px' }} />
                                        Genres:
                                                    </div>
                                                    <div style={{ paddingLeft: '8px', color: '#4B5563' }}>
                                                        {formatGenreList(categorized.genres)}
                                                    </div>
                                                </div>
                                            )}

                                            {categorized.skills.length > 0 && (
                                                <div style={{ marginBottom: '4px' }}>
                                                    <div style={{
                                                        fontWeight: 500,
                                                        fontSize: '0.875rem',
                                                        color: '#374151',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}>
                                                        <Wrench style={{ height: '14px', width: '14px', marginRight: '4px' }} />
                                        Skills:
                                                    </div>
                                                    <div style={{ paddingLeft: '8px', color: '#4B5563' }}>
                                                        {categorized.skills.join(', ')}
                                                    </div>
                                                </div>
                                            )}

                                            {categorized.artists.length === 0 &&
                                   categorized.genres.length === 0 &&
                                   categorized.skills.length === 0 && (
                                                <div style={{ color: '#4B5563', textAlign: 'center' }}>
                                      No detailed information available
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                <div style={{
                                    position: 'absolute',
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#FFFFFF',
                                    transform: 'rotate(45deg)',
                                    bottom: '-6px',
                                    left: '50%',
                                    marginLeft: '-6px',
                                    borderRight: '1px solid #E5E7EB',
                                    borderBottom: '1px solid #E5E7EB'
                                }} />
                            </div>,
                            document.body
                        )
                                            }
                                        </div>
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                ) : error ? (
                    <p className="text-center text-red-500">{error}</p>
                ) : (
                    <p className="text-center text-gray-500">{profileName ? `No people like ${profileName} yet` : 'No people to show yet'}</p>
                )}
            </div>
        </div>
    );
};

function isObjectId(str: string): boolean {
    // Check if the string matches MongoDB ObjectID format (24 hex characters)
    return /^[0-9a-f]{24}$/i.test(str);
}

function findFirstReadableAttribute(attributes: string[]): string {
    if (!attributes || attributes.length === 0) {
        return '';
    }

    // First categorize the attributes using the same logic as in the main component
    const artists: string[] = [];
    const genres: string[] = [];
    const skills: string[] = [];

    // Common genre keywords to identify genres
    const genreKeywords = [
        'rock', 'pop', 'jazz', 'hip hop', 'hip-hop', 'rap', 'electronic', 'classical',
        'folk', 'indie', 'r&b', 'country', 'metal', 'blues', 'reggae', 'disco',
        'punk', 'techno', 'house', 'ambient', 'funk', 'soul', 'edm', 'trap',
        'lofi', 'lo-fi', 'dance', 'drum and bass', 'dnb', 'dubstep', 'hardstyle',
        'trance', 'alternative', 'latin', 'k-pop', 'j-pop'
    ];

    // Skill keywords to identify skills
    const skillKeywords = [
        'producer', 'audio engineer', 'dj', 'mix', 'master', 'engineer',
        'guitarist', 'bassist', 'drummer', 'vocalist', 'singer',
        'beat', 'beatmaker', 'keys', 'piano', 'keyboard'
    ];

    // Categorize each attribute
    attributes.forEach(attr => {
        if (!attr) return;

        const lowerAttr = attr.toLowerCase();

        // Check if it's a genre
        if (genreKeywords.some(keyword =>
            lowerAttr === keyword ||
      lowerAttr.includes(keyword) ||
      keyword.includes(lowerAttr))
        ) {
            genres.push(attr);
            return;
        }

        // Check if it's a skill
        if (skillKeywords.some(keyword =>
            lowerAttr === keyword ||
      lowerAttr.includes(keyword) ||
      keyword.includes(lowerAttr))
        ) {
            skills.push(attr);
            return;
        }

        // Default to artist if not identified as genre or skill
        artists.push(attr);
    });

    // Priority order: artists, genres, skills (changed from previous order)
    if (artists.length > 0) {
        return artists[0]; // Prioritize showing artists first
    } else if (genres.length > 0) {
        return formatGenreLabel(genres[0]);
    } else if (skills.length > 0) {
        return skills[0]; // Finally show skills
    }

    // If none of the above worked, use any readable attribute
    return attributes[0] || '';
}

function countReadableAttributes(attributes: string[]): number {
    if (!attributes) return 0;
    return attributes.filter(attr => !isObjectId(attr)).length;
}

function getAllReadableAttributes(attributes: string[]): string[] {
    if (!attributes) return [];
    return attributes.filter(attr => !isObjectId(attr));
}

// Helper function to categorize attributes
function categorizeAttributes(attributes: string[], profile?: any): CategorizedAttributes {
    // Cache for categorization results to prevent reprocessing the same data
    const cacheKey = Array.isArray(attributes) ? attributes.join(',') : '';

    // Maintain a static cache for the categorizeAttributes function
    if (!('attributeCache' in window)) {
        (window as any).attributeCache = {};
    }

    // Return cached result if available
    if (cacheKey && (window as any).attributeCache[cacheKey]) {
        return (window as any).attributeCache[cacheKey];
    }

    // Return empty categories if no attributes to prevent processing empty arrays repeatedly
    if (!attributes || attributes.length === 0) {
        return {
            artists: [],
            genres: [],
            skills: []
        };
    }

    const readable = attributes.filter(attr => !isObjectId(attr));
    const result: CategorizedAttributes = {
        artists: [],
        genres: [],
        skills: []
    };

    const hasSkillIndicators = (text: string): boolean => {
        const skillKeywords = [
            'producer', 'producing', 'production',
            'mix', 'mixing', 'master', 'mastering',
            'engineer', 'engineering', 'record', 'recording',
            'play', 'player', 'playing',
            'guitarist', 'guitarist', 'singer', 'vocalist',
            'drum', 'drummer', 'beat', 'beatmaker',
            'compose', 'composer', 'write', 'writer', 'songwriter',
            'dj', 'synth', 'keyboard', 'keys',
            'bass', 'piano', 'violin',
            'artist', 'musician', 'band', 'perform',
            'design', 'designer', 'graphic',
            'rapper', 'rap', 'mc', 'emcee'  // Added rap-related terms
        ];
        return skillKeywords.some(keyword => text.toLowerCase().includes(keyword));
    };

    // Process each attribute
    for (const currentAttr of readable) {
        if (!currentAttr) continue;

        const lowerCurrentAttr = currentAttr.toLowerCase();

        if (hasSkillIndicators(currentAttr)) {
            result.skills.push(currentAttr);
            continue;
        }

        let isGenre = false;
        if (profile?.favGenres?.length > 0) {
            isGenre = profile.favGenres.some((genre: string) =>
                genre.toLowerCase() === lowerCurrentAttr ||
        lowerCurrentAttr.includes(genre.toLowerCase()) ||
        genre.toLowerCase().includes(lowerCurrentAttr)
            );
        }

        if (isGenre) {
            result.genres.push(currentAttr);
            continue;
        }

        let isArtist = false;
        if (profile?.favArtists?.length > 0) {
            isArtist = profile.favArtists.some((artist: any) => {
                const artistName = typeof artist === 'string'
                    ? artist.toLowerCase()
                    : (artist.name || artist.displayText || '').toLowerCase();
                return artistName === lowerCurrentAttr;
            });
        }

        if (isArtist) {
            result.artists.push(currentAttr);
            continue;
        }

        result.artists.push(currentAttr);
    }

    result.artists = [...new Set(result.artists)];
    result.genres = [...new Set(result.genres)];
    result.skills = [...new Set(result.skills)];

    if (cacheKey) {
        (window as any).attributeCache[cacheKey] = result;
    }

    return result;
}

export default SimilarArtists;
